import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { insideImages } from '../data/content';
export function LookInside() {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollToIndex = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(insideImages.length - 1, index));
    const slide = track.children[clamped] as HTMLElement | undefined;
    if (slide) {
      track.scrollTo({
        left: slide.offsetLeft - track.offsetLeft,
        behavior: 'smooth'
      });
    }
  }, []);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const handleScroll = () => {
      const center = track.scrollLeft + track.clientWidth / 2;
      let closest = 0;
      let smallest = Number.POSITIVE_INFINITY;
      Array.from(track.children).forEach((child, index) => {
        const slide = child as HTMLElement;
        const slideCenter = slide.offsetLeft - track.offsetLeft + slide.clientWidth / 2;
        const distance = Math.abs(slideCenter - center);
        if (distance < smallest) {
          smallest = distance;
          closest = index;
        }
      });
      setActiveIndex(closest);
    };
    track.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', handleScroll);
  }, []);
  return <section className="bg-white px-4 py-5 sm:px-6" aria-labelledby="look-inside-heading" data-id="element-93" data-mp="3iihm58tzvizi">
      <div className="mb-3 text-center" data-id="element-94" data-mp="3ign01r6zlktu">
        <p className="mb-1 text-[11px] font-extrabold uppercase tracking-widest text-brand-teal sm:text-xs" data-id="element-95" data-mp="129kbump8sey">
          Look Inside
        </p>
        <h2 id="look-inside-heading" className="mb-1 text-xl font-extrabold uppercase leading-tight text-brand-navy sm:text-3xl" data-id="element-96" data-mp="3sekl65tjv1i8">
          Discover What's Inside CLEANING READY™
        </h2>
        <p className="mx-auto max-w-2xl text-[12px] font-medium leading-snug text-brand-ink sm:text-sm" data-id="element-97" data-mp="13ov3jt1paf68">
          Explore client intake forms, residential and commercial cleaning checklists, quote
          worksheets, service-scope tools, scheduling dashboards, recurring-client trackers,
          communication templates, quality-control resources and practical cleaning business systems
          included throughout the complete package.
        </p>
      </div>

      <div className="relative" data-id="element-98" data-mp="krtyk885cf2a">
        <div ref={trackRef} className="flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="group" aria-roledescription="carousel" aria-label="Inside previews of the complete package" tabIndex={0} data-id="element-99" data-mp="pp2f64dncb1f">
          {insideImages.map((image, index) => <figure key={image.src} className="w-[85%] shrink-0 snap-center sm:w-[62%]" aria-roledescription="slide" aria-label={`Preview ${index + 1} of ${insideImages.length}`} data-id="element-100" data-mp="2a4e5hn36wpxx" data-mpi={index}>
              <img src={image.src} alt={image.alt} loading={index === 0 ? 'eager' : 'lazy'} className="w-full rounded-lg border border-brand-navy/10 bg-brand-cream object-contain shadow-md" data-id="element-101" data-mp="2faj9y1aq7ohe" />
            </figure>)}
        </div>

        <button type="button" onClick={() => scrollToIndex(activeIndex - 1)} disabled={activeIndex === 0} aria-label="Previous preview" className="absolute left-0 top-1/2 -translate-y-1/2 rounded-full border border-brand-navy/15 bg-white/95 p-2 text-brand-navy shadow-md transition-opacity duration-150 hover:bg-white disabled:pointer-events-none disabled:opacity-0 focus:outline-none focus:ring-2 focus:ring-brand-green" data-id="element-102" data-mp="21g07xqyp2mzm">
          <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-103" data-mp="3hui809nbpb02" />
        </button>
        <button type="button" onClick={() => scrollToIndex(activeIndex + 1)} disabled={activeIndex === insideImages.length - 1} aria-label="Next preview" className="absolute right-0 top-1/2 -translate-y-1/2 rounded-full border border-brand-navy/15 bg-white/95 p-2 text-brand-navy shadow-md transition-opacity duration-150 hover:bg-white disabled:pointer-events-none disabled:opacity-0 focus:outline-none focus:ring-2 focus:ring-brand-green" data-id="element-104" data-mp="3p0caarih14k3">
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-105" data-mp="3b1mhho2opr1j" />
        </button>
      </div>

      <div className="mt-1 flex items-center justify-center gap-1.5" data-id="element-106" data-mp="2wcq9ts4jig1g">
        {insideImages.map((image, index) => <button key={image.src} type="button" onClick={() => scrollToIndex(index)} aria-label={`Go to preview ${index + 1}`} aria-current={activeIndex === index} className={`h-1.5 rounded-full transition-[width,background-color] duration-150 focus:outline-none focus:ring-2 focus:ring-brand-green ${activeIndex === index ? 'w-5 bg-brand-green' : 'w-1.5 bg-brand-navy/25'}`} data-id="element-107" data-mp="3185mwhuq47wt" data-mpi={index} />)}
      </div>
      <p className="mt-1 text-center text-[10px] font-semibold uppercase tracking-wide text-brand-muted" data-id="element-108" data-mp="2hm3r74npnbb6">
        Swipe to explore {activeIndex + 1} / {insideImages.length}
      </p>
    </section>;
}